import React from 'react';
import styled from 'styled-components';

const InfoContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  height: fit-content;
`;

const Title = styled.h2`
  color: white;
  margin: 0 0 20px 0;
  font-size: 1.3rem;
  text-align: center;
`;

const AlgorithmCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const AlgorithmName = styled.h3`
  color: #4ecdc4;
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  text-align: center;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 15px;
`;

const InfoItem = styled.div`
  text-align: center;
`;

const InfoLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  margin-bottom: 5px;
`;

const InfoValue = styled.div`
  color: white;
  font-size: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
  text-align: center;
`;

const algorithmData = {
  bubble: {
    name: 'Bubble Sort',
    timeComplexity: 'O(n²)',
    spaceComplexity: 'O(1)',
    stability: 'Stable',
    description: 'Simple sorting algorithm that repeatedly steps through the list, compares adjacent elements and swaps them if they are in the wrong order.'
  },
  selection: {
    name: 'Selection Sort',
    timeComplexity: 'O(n²)',
    spaceComplexity: 'O(1)',
    stability: 'Unstable',
    description: 'Divides the input into a sorted and unsorted region, repeatedly selects the smallest element from the unsorted region.'
  },
  insertion: {
    name: 'Insertion Sort',
    timeComplexity: 'O(n²)',
    spaceComplexity: 'O(1)',
    stability: 'Stable',
    description: 'Builds the final sorted array one item at a time, by repeatedly inserting a new element into the sorted portion of the array.'
  },
  merge: {
    name: 'Merge Sort',
    timeComplexity: 'O(n log n)',
    spaceComplexity: 'O(n)',
    stability: 'Stable',
    description: 'Divide-and-conquer algorithm that recursively breaks down the problem into subproblems until they become simple enough to solve directly.'
  },
  quick: {
    name: 'Quick Sort',
    timeComplexity: 'O(n log n)',
    spaceComplexity: 'O(log n)',
    stability: 'Unstable',
    description: 'Efficient, in-place sorting algorithm that uses a divide-and-conquer strategy to sort elements around a pivot.'
  }
};

const AlgorithmInfo = ({ currentAlgorithm, comparisons, swaps }) => {
  const currentAlgo = algorithmData[currentAlgorithm] || algorithmData.bubble;

  return (
    <InfoContainer>
      <Title>Algorithm Information</Title>
      
      <AlgorithmCard>
        <AlgorithmName>{currentAlgo.name}</AlgorithmName>
        
        <InfoGrid>
          <InfoItem>
            <InfoLabel>Time Complexity</InfoLabel>
            <InfoValue>{currentAlgo.timeComplexity}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Space Complexity</InfoLabel>
            <InfoValue>{currentAlgo.spaceComplexity}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Stability</InfoLabel>
            <InfoValue>{currentAlgo.stability}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Current Comparisons</InfoLabel>
            <InfoValue>{comparisons}</InfoValue>
          </InfoItem>
        </InfoGrid>
        
        <Description>{currentAlgo.description}</Description>
      </AlgorithmCard>
      
      <AlgorithmCard>
        <AlgorithmName>Performance Metrics</AlgorithmName>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>Total Comparisons</InfoLabel>
            <InfoValue>{comparisons}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Total Swaps</InfoLabel>
            <InfoValue>{swaps}</InfoValue>
          </InfoItem>
        </InfoGrid>
      </AlgorithmCard>
      
      <AlgorithmCard>
        <AlgorithmName>Learning Tips</AlgorithmName>
        <Description>
          Watch how the algorithm progresses step by step. Notice the patterns in comparisons and swaps. 
          Try different array sizes to see how performance scales!
        </Description>
      </AlgorithmCard>
    </InfoContainer>
  );
};

export default AlgorithmInfo;
